/**
 * Admin inbox for billing-reconciliation mismatches.
 *
 * Source: `/billingReconciliation` collection — populated by the daily
 * `mollieSubscriptionReconciliation` cron when a local subscription
 * status diverges from the Mollie API's view (e.g. lokal cancelled
 * but Mollie still active, or vice versa). Each row carries the
 * customer's UID, the Mollie IDs and the two status values.
 *
 * The admin can:
 *   • inspect the row + jump to the user's billing detail page
 *   • mark it resolved with a free-form note (kept as audit log)
 *
 * Resolved rows stay in the collection but are filtered out of the
 * default view.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Check, Loader2, AlertTriangle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  useAdminReconciliation,
  useAdminResolveReconciliation,
} from '../../hooks/useBilling';
import { useToast } from '../../components/ui/toast';

export function AdminReconciliationInboxPage() {
  const list = useAdminReconciliation();
  const resolve = useAdminResolveReconciliation();
  const { toast } = useToast();
  const [showResolved, setShowResolved] = React.useState(false);
  const rows = (list.data ?? []).filter((r) => showResolved || !r.resolved);

  async function onResolve(id: string) {
    const note =
      (typeof window !== 'undefined'
        ? window.prompt('Notiz zur Resolution (optional):')
        : null) ?? '';
    try {
      await resolve.mutateAsync({ id, note });
      toast({ title: 'Erledigt', description: 'Eintrag als gelöst markiert.' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-amber-400" />
            Mollie-Reconciliation
          </CardTitle>
          <CardDescription>
            Mismatches zwischen lokalem Billing-Status und der Mollie-API. Wird
            täglich um 04:00 Wien aktualisiert. Eine "active vs. canceled"-Zeile
            heißt typischerweise: lokal cancelled aber Mollie zieht weiter ein
            (oder umgekehrt).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Input
                type="checkbox"
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
                className="h-4 w-4"
              />
              Auch erledigte anzeigen
            </label>
            <span className="text-xs text-muted-foreground">
              {list.isLoading ? 'Lade …' : `${rows.length} Einträge`}
            </span>
          </div>

          {list.isLoading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 size-4 animate-spin" /> Lade …
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Keine offenen Mismatches.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeitpunkt</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Lokal</TableHead>
                  <TableHead>Mollie</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className={r.resolved ? 'opacity-50' : undefined}>
                    <TableCell className="text-xs">
                      {r.ts
                        ? new Date(r.ts).toLocaleString('de-DE', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {r.uid ? (
                        <Link
                          to={`/admin/users/${r.uid}`}
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <code className="font-mono text-[10px]">{r.uid.slice(0, 12)}</code>
                          <ExternalLink className="size-3" />
                        </Link>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{r.localStatus ?? '—'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.remoteStatus === 'active' ? 'success' : 'muted'}>
                        {r.remoteStatus ?? '—'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground">
                      {r.mollieSubscriptionId ?? '—'}
                    </TableCell>
                    <TableCell>
                      {r.resolved ? (
                        <Badge variant="success" className="text-[10px]">
                          erledigt
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="text-[10px]">
                          offen
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!r.resolved && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void onResolve(r.id)}
                          disabled={resolve.isPending}
                        >
                          <Check className="mr-1 size-3.5" /> Erledigt
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
